/* eslint-disable require-atomic-updates */
import { useCallback, useEffect, useRef } from 'react'
import { useLazyQuery, useMutation, useReactiveVar } from '@apollo/client'
import {
  GetDemoBullseyeQuotaQuery,
  GetDemoUpDownAiQuotaQuery,
  GetGuestSessionQuery
} from '__generated__/graphql'
import { GET_GUEST_SESSION } from 'api/guest-session/get-guest-session'
import { MERGE_GUEST_SESSION } from 'api/guest-session/merge-guest-session'
import { GET_DEMO_BULLSEYE_QUOTA } from 'api/mode-bulls-eye/get-demo-bullseye-quota'
import { GET_DEMO_UP_DOWN_AI_QUOTA } from 'api/mode-up-down-ai-demo/get-demo-up-down-ai-quota'
import { isGuestClaimDialogOpenVar } from 'shared/store/dialogs'
import { userVar } from 'shared/store/user'
import {
  EMPTY_GUEST_DEMO_ROUNDS,
  guestDemoRoundsVar,
  type GuestDemoRounds
} from '../store'
import { countRounds } from '../utils/describe-claim'

const LAZY = { fetchPolicy: 'no-cache' } as const

const readRounds = (
  upDownAi?: GetDemoUpDownAiQuotaQuery['getDemoUpDownAiQuota'] | null,
  bullseye?: GetDemoBullseyeQuotaQuery['getDemoBullseyeQuota'] | null
): GuestDemoRounds => ({
  upDownAi: upDownAi?.roundsPlayed ?? 0,
  bullseye: bullseye?.roundsPlayed ?? 0
})

/** An unclaimed session: the dialog for demo rounds, else a silent merge. */
const nextStep = (
  session: GetGuestSessionQuery['getGuestSession'],
  rounds: GuestDemoRounds
) => {
  if (!session?.id || session.claimedAt) return null

  return countRounds(rounds) > 0 ? 'claim' : 'merge'
}

/**
 * Offers the demo-results claim once per signed-in user, whatever the way
 * in (wallet, Privy email / OAuth / wallet): the guest session lives in a
 * cookie on the API host, so it survives the OAuth redirect and a reload,
 * and the check simply re-runs when `userVar` is set again (FE-310).
 *
 * A guest session without demo rounds is merged silently (FE-321): the
 * backend hands the guest's attribution and cookie consent over to the user
 * only on `mergeGuestSession`. NOTE: it uses up the user's one claim
 * (`GuestSession.userId` is unique), so demo rounds played later as a guest
 * elsewhere can no longer be claimed; it goes once the backend links the
 * guest's attribution and consent at sign-in.
 */
export const useMergeGuestSession = () => {
  const user = useReactiveVar(userVar)
  const promptedForUserRef = useRef<string | null>(null)
  const [getGuestSession] = useLazyQuery(GET_GUEST_SESSION, LAZY)
  const [getUpDownAiQuota] = useLazyQuery(GET_DEMO_UP_DOWN_AI_QUOTA, LAZY)
  const [getBullseyeQuota] = useLazyQuery(GET_DEMO_BULLSEYE_QUOTA, LAZY)
  const [mergeGuestSession] = useMutation(MERGE_GUEST_SESSION)

  const mergeSilently = useCallback(async () => {
    try {
      await mergeGuestSession()
    } catch (error) {
      console.warn('Silent mergeGuestSession failed', error)
    }
  }, [mergeGuestSession])

  const promptIfNeeded = useCallback(async () => {
    if (!user?.id || promptedForUserRef.current === user.id) return
    promptedForUserRef.current = user.id

    const [session, upDownAi, bullseye] = await Promise.all([
      getGuestSession(),
      getUpDownAiQuota(),
      getBullseyeQuota()
    ])
    const rounds = readRounds(
      upDownAi.data?.getDemoUpDownAiQuota,
      bullseye.data?.getDemoBullseyeQuota
    )
    const step = nextStep(session.data?.getGuestSession, rounds)
    if (step === 'merge') await mergeSilently()
    if (step !== 'claim') return

    guestDemoRoundsVar(rounds)
    isGuestClaimDialogOpenVar(true)
  }, [
    getBullseyeQuota,
    getGuestSession,
    getUpDownAiQuota,
    mergeSilently,
    user?.id
  ])

  useEffect(() => {
    if (!user?.id) {
      promptedForUserRef.current = null
      guestDemoRoundsVar(EMPTY_GUEST_DEMO_ROUNDS)
      isGuestClaimDialogOpenVar(false)
      return
    }

    void promptIfNeeded()
  }, [promptIfNeeded, user?.id])
}
